import { Dispatch, SetStateAction, useEffect, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ctxPluto } from '@/contexts/PlutoContext';
import { Popup } from '@/components/common/Popup';
import TargetSelectorOpenerButton from '@/components/common/TargetSelectorOpenerButton';
import { ctxUser } from '@/contexts/UserContext';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

import { BottomSheet } from '@/components/common/BottomSheet';
import { HeaderNameAndIcons } from '@/components/common/HeaderNameAndIcons';
import TargetTrackingPanel from './TargetTrackingPanel';
import TrackingTargetSelector from './TrackingTargetSelector';
import { trackedTargetLimit } from '@/tmp/TargetService';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';

import '@/components/common/Icons.css';
import './TrackingPopup.css';
import Box from '@mui/material/Box';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { MinimalTarget } from '@/generated/model';
import FloatingBottomButton from '@/components/common/FloatingBottomButton';

// target selector popup gorgetheto body resze
function Body({
  trackingLimit,
  setShowTargetSelector,
}: {
  trackingLimit: number;
  setShowTargetSelector: Dispatch<SetStateAction<boolean>>;
}) {
  function InfiniteList() {
    return <TrackingTargetSelector trackingLimit={trackingLimit} setShowTargetSelector={setShowTargetSelector} />;
  }

  if (isPhoneOrHighScreen()) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <InfiniteList />
      </Box>
    );
  }

  return (
    <div className="tracking-body-target" data-qa-element-id="multi_element_selector_panel">
      <InfiniteList />
    </div>
  );
}

// popup, a kivalasztott target listat modositja
export default function TrackingPopup() {
  const ctx = useContext(ctxPluto);
  const userCtx = useContext(ctxUser);
  const { t } = useTranslation();
  const [trackingLimit, setTrackingLimit] = useState<number | undefined>(undefined);
  const [initialSelection, setInitialSelection] = useState<MinimalTarget[]>([]);
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [isVehicleSelected, setIsVehicleSelected] = useState(false);
  const [isSelectionChanged, setIsSelectionChanged] = useState(false);
  const target = ctx?.fullTargets?.find((t) => t.target.id === ctx?.popupTargetId);

  useEffect(() => {
    async function getTrackingLimit() {
      try {
        const response = await trackedTargetLimit();
        if (response.status === 200) {
          if (!response.data) {
            console.debug('tracked target limit is missing');
            setTrackingLimit(50); // FIXME put sane defaults into separate file
          } else {
            setTrackingLimit(response.data);
          }
        }
      } catch (error) {
        rethrowIfNotClientException(error);
      }
    }
    if (userCtx?.isToken()) {
      setTrackingLimit(1);
      return;
    }
    void getTrackingLimit();
  }, [userCtx]);

  useEffect(() => {
    ctx?.setMode('target'); // show selectedTargets on the map
  }, [ctx]);

  useEffect(() => {
    if (ctx?.fullTargets !== undefined) {
      setIsVehicleSelected(true);
    } else {
      setIsVehicleSelected(false);
    }
  }, [ctx?.fullTargets]);

  useEffect(() => {
    if (!target) ctx?.setPopupTargetId(undefined);
  }, [target]);

  const body = useMemo(() => {
    if (!trackingLimit) return <></>;
    return <Body trackingLimit={trackingLimit} setShowTargetSelector={setShowTargetSelector} />;
  }, [trackingLimit]);

  function onOpenTargetSelector(stateValue: boolean) {
    setShowTargetSelector(stateValue);
    setInitialSelection(ctx?.selectedTargets ?? []);
  }

  useEffect(() => {
    const initialTargetIds = initialSelection.map((target) => target.id).sort((a, b) => a - b);

    const selectedTargetIds = (ctx?.selectedTargets ?? []).map((target) => target.id).sort((a, b) => a - b);

    const isDifferent =
      initialTargetIds.length !== selectedTargetIds.length ||
      initialTargetIds.some((id, index) => id !== selectedTargetIds[index]);

    setIsSelectionChanged(isDifferent);
  }, [ctx?.selectedTargets, initialSelection]);

  async function onClose() {
    setShowTargetSelector(false);
    ctx?.setFocusedTarget(null);
  }

  // a header is scrollozodjon es a body is
  // akkor kellene a header max height ?
  return isPhoneOrHighScreen() ? (
    <>
      {!userCtx?.isToken() && (
        <TargetSelectorOpenerButton
          setOnClicked={onOpenTargetSelector}
          targetSelected={!!target}
          isVehicleOrRouteSelected={isVehicleSelected}
          title={t('mainMenu.tracking')}
        />
      )}
      {showTargetSelector && (
        <Popup
          popupTitle={t('mainMenu.tracking')}
          header={undefined}
          body={body}
          footer={
            isSelectionChanged ? (
              <FloatingBottomButton
                title={t('tracking.map')}
                icon={<MapOutlinedIcon sx={{ mr: 1 }} />}
                onAction={() => onClose()}
                hasBorder={true}
                bottom={12}
                position="absolute"
              />
            ) : undefined
          }
          closePopup={onClose}
          isFullScr={true}
          leftText={true}
        />
      )}
      {ctx?.popupTargetId && target && (
        <BottomSheet
          key={ctx.popupTargetId}
          header={
            <HeaderNameAndIcons
              target={target.target}
              onClose={() => ctx?.setPopupTargetId(undefined)}
              onReload={() => ctx?.setImmediateLoadTargetId(ctx?.popupTargetId)}
              hasTargetMetadataId={target.hasTachographMetadataId}
            />
          }
          setBottomSheetUp={ctx.setIsBottomSheetOpen}
        >
          <TargetTrackingPanel />
        </BottomSheet>
      )}
    </>
  ) : (
    <>
      <div className="tracking-panel">{body}</div>
    </>
  );
}
